<div class="dialog-header">
    <div>
        <h2 mat-dialog-title>Manage FX Rates</h2>
        <div class="sub">
            Deal currency: <b>{{ dealCurrency ?? "—" }}</b>
            &nbsp;·&nbsp; <span class="muted">grouped by currency pair</span>
        </div>
    </div>
    <button mat-icon-button class="dialog-close" aria-label="Close" data-test="dialog-close" (click)="close()">
        <mat-icon>close</mat-icon>
    </button>
</div>

<div class="date-strip">
    <div class="date-field">
        <label>Rate date <span class="auto-note">— rates auto-fetch on change</span></label>
        <div class="date-input-row">
            <input type="date" [max]="today" [value]="chosenDate"
                   (change)="onDateChange($any($event.target).value)" data-test="fx-rate-date"/>
            <button mat-button color="primary" (click)="useToday()">Use today ({{ today }})</button>
        </div>
    </div>
    <div class="ecb-note">
        Rates are based on ECB data published ~16:00 CET. Weekend/holiday dates resolve to the most recent prior
        business day; the actual returned date is shown per pair.
    </div>
</div>

<div class="global-bar">
    <span class="gb-label">Stage rates for <b>every pair</b>:</span>
    <button mat-stroked-button color="primary" [disabled]="isFetching" (click)="autoApplyLatest()">
        <mat-icon>auto_awesome</mat-icon>
        Auto-apply latest
    </button>
    <button mat-stroked-button color="primary" [disabled]="isFetching" (click)="applyAtChosen()">
        <mat-icon>event</mat-icon>
        Apply rates &#64; {{ chosenDate }}
    </button>
    <button mat-button color="primary" [disabled]="isFetching" (click)="clearStaging()">Clear staging</button>
    <span class="gb-basis">{{ basisLabel }}</span>
</div>

@if (blockingErrors.total > 0) {
    <div class="issues-banner" data-test="fx-issues-banner">
        <mat-icon>block</mat-icon>
        <span><b>Cannot apply</b> —
            @if (blockingErrors.missingCcy) {
                {{ blockingErrors.missingCcy }} missing currency
            }
            @if (blockingErrors.missingCcy && blockingErrors.unpriceable) {
                ·
            }
            @if (blockingErrors.unpriceable) {
                {{ blockingErrors.unpriceable }} not available (ECB)
            }.
            Set the currency or enter a manual rate to unblock.</span>
    </div>
}

@if (sameCurrencyIssues > 0) {
    <div class="issues-banner warn" data-test="fx-same-ccy-banner">
        <mat-icon>warning</mat-icon>
        <span><b>{{ sameCurrencyIssues }} same-currency rate{{ sameCurrencyIssues === 1 ? "" : "s" }}</b>
            {{ sameCurrencyIssues === 1 ? "is" : "are" }} not 1.0000 — they'll be corrected to 1.0000 on Apply.</span>
    </div>
}

<mat-dialog-content class="fx-body">
    @if (isFetching) {
        <div class="fetch-overlay">
            <mat-spinner diameter="30"></mat-spinner>
            <div>Fetching ECB rates for {{ chosenDate }}…</div>
        </div>
    }

    <!-- Needs-currency section -->
    @if (needsCurrencyMembers.length > 0) {
        <div class="needs-ccy">
            <div class="needs-ccy-head">
                <mat-icon>warning</mat-icon>
                Needs currency — set before a rate can be applied ({{ needsCurrencyMembers.length }})
            </div>
            <table class="nc-table">
                <tbody>
                    @for (m of needsCurrencyMembers; track m.kind + m.id) {
                        <tr>
                            <td class="kind-cell"><span class="kind-chip" [class.fund]="m.kind === 'fund'" [class.asset]="m.kind === 'asset'">{{ m.kind }}</span></td>
                            <td>
                                <span class="nm">{{ m.name }}</span>
                                @if (m.kind === 'asset' && m.fundName) {
                                    <div class="sub-in">in {{ m.fundName }}</div>
                                }
                            </td>
                            <td>
                                <mat-select class="ccy-picker" placeholder="set ccy…"
                                            (selectionChange)="setMemberCurrency(m, $event.value)">
                                    @for (option of currencyOptions; track option.code) {
                                        <mat-option [value]="option.code">{{ option.code }}</mat-option>
                                    }
                                </mat-select>
                                @if (m.to) {
                                    <span class="ccy-arrow">→ {{ m.to }}</span>
                                }
                            </td>
                            <td><span class="pill missing">set {{ m.kind }} currency to enable</span></td>
                        </tr>
                    }
                </tbody>
            </table>
        </div>
    }

    <!-- Pair groups -->
    @for (group of pairGroups; track group.key) {
        <div class="pair-group" [class.same]="group.same" [class.unpriceable]="group.unpriceable">
            <div class="pair-head">
                <div class="left">
                    <span class="pair-chip" [class.same]="group.same" [class.unpriceable]="group.unpriceable" [style.background]="pairChipColor(group)">{{ group.from }}
                        → {{ group.to }}</span>
                    <mat-icon class="pair-info" matTooltip="Only assets with FOV are displayed; DMV assets have fund currency by default">info_outline
                    </mat-icon>
                    <span class="grp-meta">{{ group.members.length }} {{ group.members.length === 1 ? "member" : "members" }}</span>
                    @if (group.inconsistent) {
                        <span class="grp-flag"
                              matTooltip="The stored rate differs across members of this pair. Staging one rate resolves it. (Informational — not the blocking conflict.)">inconsistent rate in use</span>
                    }
                </div>
                <div class="pair-rates">
                    @if (group.same) {
                        <div class="rate-fact"><b>1.0000</b>same currency</div>
                    } @else if (group.unpriceable) {
                        <div class="rate-fact err"><b>n/a</b>not priced by ECB</div>
                        @if (group.runtime.staged) {
                            <div class="staged-fact"><b>staged {{ group.runtime.stagedVal | number: "1.4-4" }}</b> manual</div>
                            <button mat-button color="warn" (click)="unstage(group.key)">clear</button>
                        }
                    } @else {
                        <div class="rate-fact">
                            <b>{{ group.runtime.latest !== null ? (group.runtime.latest | number: "1.4-4") : "…" }}</b>latest
                        </div>
                        <div class="rate-fact">
                            <b>{{ group.runtime.historical === null ? "…" : (group.runtime.historical === "NA" ? "n/a" : (group.runtime.historical | number: "1.4-4")) }}</b>&#64; {{ chosenDate }}
                        </div>
                        @if (group.runtime.staged) {
                            <div class="staged-fact">
                                <b>staged {{ group.runtime.stagedVal | number: "1.4-4" }}</b>
                                {{ group.runtime.basis === "latest" ? "latest" : "@ " + group.runtime.basis }}
                            </div>
                            <button mat-button color="primary" (click)="unstage(group.key)">unstage</button>
                        }
                    }
                </div>
            </div>

            <table class="members">
                <thead>
                <tr>
                    <th>Type</th>
                    <th>Name</th>
                    <th class="num">Rate in use</th>
                    <th class="num">% diff (to latest rate)</th>
                    <th>Status</th>
                </tr>
                </thead>
                <tbody>
                    @for (m of visibleMembers(group); track m.kind + m.id) {
                        <tr [class.staged-row]="willWrite(m, group)">
                            <td><span class="kind-chip" [class.fund]="m.kind === 'fund'" [class.asset]="m.kind === 'asset'">{{ m.kind }}</span></td>
                            <td>
                                <span class="nm">{{ m.name }}</span>
                                @if (m.ccySet) {
                                    <span class="new-chip">new {{ m.from }}</span>
                                }
                                @if (m.kind === 'asset' && m.fundName) {
                                    <div class="sub-in">in {{ m.fundName }}</div>
                                }
                            </td>
                            <td class="num">
                                @if (group.same) {
                                    @if (m.current === null) {
                                        <span class="rate-num">1.0000</span>
                                        <div class="src-line">same currency</div>
                                    } @else {
                                        <span class="rate-num">{{ m.current | number: "1.4-4" }}</span>
                                        <div class="src-line" [class.stale]="m.current !== 1.0">{{ m.current === 1.0 ? "same currency" : "should be 1.0000" }}</div>
                                    }
                                } @else if (group.unpriceable) {
                                    <input type="number" step="0.0001" min="0" class="manual-rate" placeholder="manual"
                                           [value]="group.runtime.manual ?? ''"
                                           (change)="setManual(group.key, $any($event.target).value)"/>
                                    @if (manualError(group.key); as err) {
                                        <div class="src-line err">{{ err }}</div>
                                    } @else {
                                        <div class="src-line">manual entry</div>
                                    }
                                } @else if (m.current === null) {
                                    <span class="pill missing">no rate set</span>
                                } @else {
                                    <span class="rate-num">{{ m.current | number: "1.4-4" }}</span>
                                    <div class="src-line" [class.stale]="m.stale">{{ m.source }}{{ m.stale ? " · stale" : "" }}</div>
                                }
                            </td>
                            <td class="num">
                                @let diff = pctDiff(m.current, group.runtime.latest);
                                @if (!group.same && diff !== null) {
                                    <span class="pct" [class.up]="diff > 0" [class.down]="diff < 0">
                                        {{ diff > 0 ? "+" : "" }}{{ (diff * 100) | number: "1.2-2" }}%
                                    </span>
                                } @else {
                                    <span class="muted">—</span>
                                }
                            </td>
                            <td>
                                @if (group.same) {
                                    @if (m.current === 1.0) {
                                        <span class="pill idle">already correct (1.0)</span>
                                    } @else {
                                        <span class="pill staged">staged 1.0000</span>
                                    }
                                } @else if (group.runtime.staged) {
                                    <span class="pill staged">staged {{ group.runtime.stagedVal | number: "1.4-4" }}</span>
                                } @else if (group.unpriceable) {
                                    <span class="pill na">not available (ECB)</span>
                                } @else if (m.current === null) {
                                    <span class="pill missing">rate missing</span>
                                } @else if (m.stale) {
                                    <span class="pill missing">stale</span>
                                } @else {
                                    <span class="pill idle">up to date</span>
                                }
                            </td>
                        </tr>
                    }
                </tbody>
            </table>

            @if (group.same && (hiddenMemberCount(group) > 0 || isExpanded(group.key))) {
                <div class="expand-row">
                    <button mat-button color="primary" (click)="toggleGroup(group.key)">
                        {{
                            isExpanded(group.key)
                                ? "Show only issues"
                                : "Show all " + group.members.length + " members (" + hiddenMemberCount(group) + " at 1.0000 hidden)"
                        }}
                    </button>
                </div>
            }

            @if (group.unpriceable && !group.runtime.staged) {
                <div class="grp-notice">
                    <mat-icon>warning</mat-icon>
                    ECB can't price {{ group.from }} → {{ group.to }} — enter a rate in the <b>Rate in use</b> cell
                    (saved with source "user") to stage this pair, or re-set the members' currency.
                </div>
            }
        </div>
    } @empty {
        @if (needsCurrencyMembers.length === 0) {
            <div class="empty">No funds or assets to manage.</div>
        }
    }
</mat-dialog-content>

<mat-dialog-actions class="fx-footer">
    <div class="footer-warn">
        @if (canApply && stagedCounts.total > 0) {
            <span>Staged rates will overwrite the existing values for {{ stagedCounts.fundRows }} fund + {{ stagedCounts.assetRows }} asset member(s).</span>
        }
    </div>
    <button mat-flat-button class="apply-button" [disabled]="!canApply" (click)="apply()" data-test="fx-apply">
        {{ applyButtonLabel }}
    </button>
</mat-dialog-actions>
